import { BreakpointObserver } from "@angular/cdk/layout";
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatSidenavModule } from "@angular/material/sidenav";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { Settings } from "luxon";
import { JoyrideModule } from "ngx-joyride";
import { Subscription } from "rxjs";
import { AccessRequest } from "../core/models/access-request.model";
import { Filters } from "../core/models/filters.model";
import { PageHeader } from "../core/models/shared.model";
import { AppStore } from "../core/stores/app.store";
import { AuthStore } from "../core/stores/auth.store";
import { DashboardStore } from "../core/stores/dashboard.store";
import { FiltersStore } from "../core/stores/filters.store";
import { LifeCycleStore } from "../core/stores/lifecycle.store";
import { MeterStore } from "../core/stores/meter.store";
import { PlacesStore } from "../core/stores/places.store";
import { SharedStore } from "../core/stores/shared.store";
import { ShowerheadStore } from "../core/stores/showerhead.store";
import { UserStore } from "../core/stores/user.store";
import { AccessRequestDialogComponent } from "../dashboard/components/access-request-dialog/access-request-dialog.component";
import { GlobalLoadingSpinnerComponent } from "../shared/components/global-loading-spinner/global-loading-spinner.component";
import { NavButtonComponent } from "../shared/components/nav-button/nav-button.component";

const LOG_TAG = "[LAYOUT] ";
@Component({
  selector: "app-layout",
  templateUrl: "./layout.component.html",
  styleUrls: ["./layout.component.scss"],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatSidenavModule,
    JoyrideModule,
    MatProgressBarModule,
    MatIconModule,
    RouterModule,
    MatButtonModule,
    GlobalLoadingSpinnerComponent,
    NavButtonComponent,
  ],
})
export class LayoutComponent implements OnInit, OnDestroy {
  protected authStore: AuthStore = inject(AuthStore);
  protected lifecycleStore: LifeCycleStore = inject(LifeCycleStore);
  protected userStore: UserStore = inject(UserStore);
  protected appStore: AppStore = inject(AppStore);
  protected filtersStore: FiltersStore = inject(FiltersStore);
  protected placesStore: PlacesStore = inject(PlacesStore);
  protected metersStore: MeterStore = inject(MeterStore);
  protected showerheadsStore: ShowerheadStore = inject(ShowerheadStore);
  protected sharedStore: SharedStore = inject(SharedStore);
  protected dashboardStore: DashboardStore = inject(DashboardStore);

  private _route: ActivatedRoute = inject(ActivatedRoute);
  private _router: Router = inject(Router);
  // private _dateSelectorService: DateSelectorService =
  //   inject(DateSelectorService);
  private _bpObserver: BreakpointObserver = inject(BreakpointObserver);
  private _dialog: MatDialog = inject(MatDialog);

  public activeTab = signal<string>(null);
  public subActiveTab = signal<string>(null);
  protected accessRequesting = signal<boolean>(false);
  public logoutLoading = signal<boolean>(false);

  // public get datePanelOpened() {
  //   return this._dateSelectorService
  //     ? this._dateSelectorService.panelOpened
  //     : false;
  // }

  // public set datePanelOpened(value: boolean) {
  //   if (value) this._dateSelectorService.openPanel();
  //   else this._dateSelectorService.closePanel();
  // }

  private routerSub: Subscription;
  private panelSub: Subscription;
  private mobileSub: Subscription;
  private menuSub: Subscription;
  // private static _sideMenuReducedUserChoice = false;
  private redirecting: boolean = false;

  // ----- LIFE CYCLE METHODS -----

  public constructor() {
    // update timezone from userData
    effect(() => {
      const userLoading = this.userStore.loading$();
      const userTimezone = this.userStore.userTimezone$();

      if (
        !userLoading &&
        userTimezone
        /*&&
        ((typeof Settings.defaultZone == "string" &&
          userTimezone != Settings.defaultZone) ||
          typeof Settings.defaultZone !== "string")*/
      ) {
        console.log(
          LOG_TAG + "EFFECT => set default timezone",
          "from=",
          Settings.defaultZone,
          "to=",
          userTimezone
        );

        Settings.defaultZone = userTimezone;
      }
    });
    // update locale from userData
    effect(() => {
      const userLoading = this.userStore.loading$();
      const userLocale = this.userStore.userLocale$();

      if (!userLoading && userLocale && userLocale !== Settings.defaultLocale) {
        console.log(LOG_TAG + "EFFECT => set default locale");

        Settings.defaultLocale = userLocale;
      }
    });
    // display next access request dialog if necessary
    effect(
      () => {
        const loadingAccessRequests = this.userStore.loadingAccessRequests$();
        const accessRequests = this.userStore.accessRequests$();
        const accessRequestsDialogShowed =
          this.userStore.accessRequestsDialogShowed$();

        console.log(
          LOG_TAG + "EFFECT => access request dialog ??",
          loadingAccessRequests,
          accessRequests,
          accessRequestsDialogShowed
        );

        if (
          !loadingAccessRequests &&
          !accessRequestsDialogShowed &&
          accessRequests.length > 0
        ) {
          console.log(LOG_TAG + "EFFECT => show access request dialog");
          this._showRequestDialog(accessRequests[0]);
          this.userStore.updateAccessRequestsDialogShowed(true);
        }
      },
      { allowSignalWrites: true }
    );
    // redirect to login
    effect(
      () => {
        const logged = this.authStore.logged$();
        if (logged === false && !this.redirecting) {
          console.log(LOG_TAG + "EFFECT => redirect to login on logged out");

          this.redirecting = true;
          this._router.navigate(["login"], { replaceUrl: true });
        }
      },
      { allowSignalWrites: true }
    );
  }

  public ngOnInit() {
    // load user data
    this.userStore.fetchUser();
    this.userStore.fetchAccessRequests();

    // load user places
    this.placesStore.fetchPlaces();

    // update menu item selected from routing
    this._setActiveTab();
    this._setSubActiveTab();
    this.routerSub = this._router.events.subscribe((event) => {
      this._setActiveTab();
      this._setSubActiveTab();
    });

    // // update main-menu state based on filter panel
    // //TODO adapter pour filter panel
    // this.panelSub = this._dateSelectorService.panelOpenRequest.subscribe(
    //   (open) => {
    //     if (!this.appStore.mobileMode$())
    //       setTimeout(() => {
    //         //open/close menu only if user has not changed it.
    //         if (!LayoutComponent._sideMenuReducedUserChoice) {
    //           this.appStore.updateSideMenuState(open);
    //         }
    //       });
    //   }
    // );

    // update mobile / desktop mode based on screen size
    if (this.mobileSub) this.mobileSub.unsubscribe();
    this.mobileSub = this._bpObserver
      .observe(["(max-width: 800px)"])
      .subscribe((res) => {
        if (res.matches) {
          console.log(LOG_TAG + "MOBILE MODE ????");
          this.appStore.updateMainMenuState(false);
          this.appStore.updateMobileMode(true);
        } else {
          this.appStore.updateMainMenuState(true);
          this.appStore.updateMobileMode(false);
        }
      });

    if (this.menuSub) this.menuSub.unsubscribe();
    this.menuSub = this._bpObserver
      .observe(["(min-width: 801px) and (max-width: 1200px)"])
      .subscribe((res) => {
        if (res.matches) {
          this.appStore.updateSideMenuState(true);
        } else {
          this.appStore.updateSideMenuState(false);
        }
      });

    // close filter panel based on routing
    // let noDateSelector = this._route.firstChild.data["value"]["noDateSelector"];
    // if (noDateSelector) {
    // this._dateSelectorService.closePanel();
    // this.filtersStore.updatedPanelState(false);
    // const filters = this.filtersStore.filters$();
    // const panelOpened = this.filtersStore.panelOpened$();
    // this.filtersStore.updateFiltersInUrl(filters, false);
    // }
  }

  public ngOnDestroy(): void {
    if (this.routerSub) this.routerSub.unsubscribe();
    if (this.panelSub) this.panelSub.unsubscribe();
    if (this.mobileSub) this.mobileSub.unsubscribe();
    if (this.menuSub) this.menuSub.unsubscribe();
  }

  // ----- INTERATION METHODS -----

  public onLogoutClicked() {
    this.logoutLoading.set(true);
    this.authStore.logout();
  }

  public onFiltersChanged(filters: Filters) {
    console.log(LOG_TAG + "filters changed", filters);
    this.filtersStore.updateFiltersInUrl(filters);
  }

  public filterNavOpenStateChanged(opened) {
    console.log(LOG_TAG + "filterNavOpenStateChanged", opened);
    //TODO need this ?
  }

  public onSubItemClicked(subMenu: PageHeader) {
    console.log(LOG_TAG + "sub menu clicked", subMenu);
    this._router.navigate([subMenu.url], {
      replaceUrl: true,
      queryParamsHandling: "merge",
    });
  }

  public mainMenuOpenedChanged(opened) {
    console.log(LOG_TAG + "mainMenuOpenedChanged", opened);
    this.appStore.updateMainMenuState(opened);
  }

  public toggleSideMenu() {
    this.appStore.updateSideMenuState(!this.appStore.sideMenuReduced$());
  }

  // ----- PRIVATE METHODS -----

  private _setActiveTab() {
    this.activeTab.set(this._route.firstChild.data["value"]["activeTab"]);
  }

  private _setSubActiveTab() {
    this.subActiveTab.set(this._route.firstChild.data["value"]["subActiveTab"]);
  }

  private _showRequestDialog(accessRequest: AccessRequest) {
    const dialogRef = this._dialog.open(AccessRequestDialogComponent, {
      data: accessRequest,
    });

    dialogRef.afterClosed().subscribe(() => {
      this.userStore.updateAccessRequestsDialogShowed(false);
    });
  }
}
